import PageHero from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { SITE } from "../lib/siteConfig";

const LegalShell = ({ testid, eyebrow, titleLines, description, sections }) => (
  <main>
    <PageHero testid={testid} eyebrow={eyebrow} titleLines={titleLines} description={description} />
    <section className="px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto pb-20 lg:pb-28">
      <div className="space-y-10">
        {sections.map((s, i) => (
          <Reveal key={s.title} delay={Math.min(i * 0.05, 0.3)}>
            <div data-testid={`${testid}-section-${i + 1}`}>
              <h2 className="font-display text-lg sm:text-xl font-semibold text-white mb-3">
                <span className="font-mono-brand text-xs text-[#D4AF37] mr-3">{String(i + 1).padStart(2, "0")}</span>
                {s.title}
              </h2>
              {s.paragraphs.map((p, j) => (
                <p key={j} className="text-sm sm:text-base text-[#A1A1AA] leading-relaxed mb-3">{p}</p>
              ))}
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal delay={0.1} className="mt-14">
        <div className="rounded-2xl border border-[rgba(212,175,55,0.2)] bg-[#121216] p-6">
          <p className="text-xs text-[#71717A] leading-relaxed">
            For any questions regarding this page, contact us at{" "}
            <a href={`mailto:${SITE.email}`} data-testid={`${testid}-contact-email`} className="text-[#E6CA65] hover:text-[#F3E5AB]">
              {SITE.email}
            </a>{" "}
            or call {SITE.phone}. Last updated: July 2026.
          </p>
        </div>
      </Reveal>
    </section>
  </main>
);

export function PrivacyPolicy() {
  return (
    <LegalShell
      testid="privacy-policy"
      eyebrow="Legal"
      titleLines={["Privacy Policy"]}
      description="How CREDNOVA SERVICES PRIVATE LIMITED collects, uses and protects the information you share with us."
      sections={[
        {
          title: "Information We Collect",
          paragraphs: [
            "When you submit an enquiry through our website, we collect the details you provide — such as your name, phone number, email address, loan category, desired amount and any message you choose to share.",
            "We may also collect basic technical information (such as browser type and pages visited) to improve the performance and security of our website.",
          ],
        },
        {
          title: "How We Use Your Information",
          paragraphs: [
            "Your information is used to respond to your enquiry, understand your loan requirement, provide suitable guidance, and coordinate with banks or financial institutions on your behalf where you have asked us to do so.",
            "We may contact you by phone, email or message regarding your enquiry and related assistance.",
          ],
        },
        {
          title: "Sharing of Information",
          paragraphs: [
            "We share your details with banks, NBFCs or financial institutions only where it is necessary to process your loan enquiry and only with your consent.",
            "We do not sell, rent or trade your personal information to any third party for marketing purposes.",
          ],
        },
        {
          title: "Data Security",
          paragraphs: [
            "We apply reasonable technical and organisational safeguards to protect your information against unauthorised access, loss or misuse. However, no method of transmission over the internet is completely secure, and we encourage you to avoid sharing sensitive credentials (such as passwords, OTPs or card details) through our forms.",
          ],
        },
        {
          title: "Your Rights",
          paragraphs: [
            "You may request access to, correction of, or deletion of your personal information at any time by writing to us using the contact details provided on this website. We will address genuine requests within a reasonable timeframe, subject to applicable law.",
          ],
        },
        {
          title: "Changes to This Policy",
          paragraphs: [
            "We may update this Privacy Policy from time to time. The updated version will be posted on this page with a revised date. Continued use of the website after changes indicates acceptance of the updated policy.",
          ],
        },
      ]}
    />
  );
}

export function Terms() {
  return (
    <LegalShell
      testid="terms"
      eyebrow="Legal"
      titleLines={["Terms & Conditions"]}
      description="The terms that govern your use of the CREDNOVA SERVICES PRIVATE LIMITED website and services."
      sections={[
        {
          title: "Scope of Services",
          paragraphs: [
            "CREDNOVA SERVICES PRIVATE LIMITED provides loan assistance and facilitation services. We help customers understand loan products, prepare documentation and connect with banks and financial institutions. We are not a bank, NBFC or lender, and we do not provide loans directly.",
          ],
        },
        {
          title: "No Guarantee of Approval",
          paragraphs: [
            "All loan approvals, rejections, interest rates, tenures and disbursals are decided solely by the respective bank or financial institution, based on their eligibility criteria, credit assessment and internal policies. We do not guarantee, and have never represented, that any application will be approved.",
          ],
        },
        {
          title: "User Responsibilities",
          paragraphs: [
            "By submitting an enquiry, you confirm that the information you provide is accurate and complete to the best of your knowledge, and that you consent to being contacted by our team regarding your enquiry.",
            "You agree not to misuse this website, submit false or misleading information, or attempt to disrupt its operation.",
          ],
        },
        {
          title: "Service Charges",
          paragraphs: [
            "Where any service charge applies, it will be communicated to you clearly and upfront before you decide to proceed. No hidden charges are levied.",
          ],
        },
        {
          title: "Intellectual Property",
          paragraphs: [
            "All content on this website — including text, design, graphics and branding — is the property of CREDNOVA SERVICES PRIVATE LIMITED and may not be reproduced without written permission.",
          ],
        },
        {
          title: "Limitation of Liability",
          paragraphs: [
            "To the extent permitted by law, CREDNOVA SERVICES PRIVATE LIMITED is not liable for decisions made by banks or financial institutions, delays in their processes, or any loss arising from reliance on general information provided on this website.",
          ],
        },
        {
          title: "Governing Law",
          paragraphs: [
            "These terms are governed by the laws of India. Any disputes shall be subject to the jurisdiction of the courts where our registered office is located.",
          ],
        },
      ]}
    />
  );
}

export function Disclaimer() {
  return (
    <LegalShell
      testid="disclaimer"
      eyebrow="Legal"
      titleLines={["Disclaimer"]}
      description="Important information about the nature of our services. Please read this carefully before submitting an enquiry."
      sections={[
        {
          title: "We Are Not a Bank or NBFC",
          paragraphs: [
            "CREDNOVA SERVICES PRIVATE LIMITED is a loan assistance and facilitation service provider. We are not a bank, a non-banking financial company (NBFC), or a lender of any kind. We do not accept deposits, issue loans, or make credit decisions.",
          ],
        },
        {
          title: "Loan Approval Rests With the Institution",
          paragraphs: [
            "Every loan application is assessed and approved (or declined) solely by the respective bank or financial institution, based on their eligibility criteria, credit checks, verification and internal policies. Our role is limited to guidance, documentation support and coordination.",
          ],
        },
        {
          title: "No Guaranteed Approval",
          paragraphs: [
            "Nothing on this website — or said by any member of our team — should be interpreted as a promise or guarantee of loan approval, a specific interest rate, or a particular loan amount. Any such guarantee would be misleading, and we do not make one.",
          ],
        },
        {
          title: "Not Financial Advice",
          paragraphs: [
            "The information on this website is general in nature and provided for guidance only. It does not constitute financial, investment or legal advice. Please evaluate your own financial situation and, where appropriate, consult a qualified advisor before making borrowing decisions.",
          ],
        },
        {
          title: "Third-Party Products and Terms",
          paragraphs: [
            "Loan products are offered by third-party banks and financial institutions and are subject to their own terms, conditions, charges and policies. We encourage you to read the institution's official documentation carefully before signing any agreement.",
          ],
        },
        {
          title: "Accuracy of Information",
          paragraphs: [
            "While we strive to keep the information on this website accurate and current, loan products, interest rates and institutional policies change frequently. Information here may not reflect the latest offerings of any institution.",
          ],
        },
      ]}
    />
  );
}
